import React, { useEffect, useRef, useState } from "react";
import "./SkinAssessmentHome.scss"
import SkinAssessmentHeader from "./components/skinAssessmentHeader/SkinAssessmentHeader";
import config from "../../../config";
import { useDispatch, useSelector } from "react-redux";
import { deleteHnWAssessmentDetail, getHnWAssessmentDetail, getHnWAssessmentHistoryLog, getHnWAssessmentList, resetAssessmentScore, resetSkinAssessmentAnswers } from "../../../actions";
import TermsBottomSheetModal from "./components/termsBottomSheetModal/TermsBottomSheetModal";
import SkinAssessmentHomeFooter from "./components/skinAssessmentFooter/SkinAssessmentHomeFooter";
import { useLocation } from "react-router-dom";
import SkinAssessmentErrorModal from "./components/skinAssessmentErrorModal/SkinAssessmentErrorModal";
import { ASSESSMENT_MODES, SKIN_ASM_CONSTANTS } from "../../../common/skinAssessmentConstant";
import { ASSESSMENT_STATUS, BusinessNatureType } from "../../../common/commonConstant";
import { SkeletonLoader } from "../../../common";
import useReturnNavigation from "../../../hooks/useReturnNavigation";

const liff = window.liff;

export const SkinAssessmentHome = ({ routeTo }) => {
    const checkboxRef = useRef(null);
    const hasDataFetched = useRef(false);
    const { search } = useLocation();
    const urlParams = new URLSearchParams(search);
    const dispatch = useDispatch();
    useReturnNavigation();

    const { assessmentList } = useSelector((state) => state.assessment);

    const [showTerms, setShowTerms] = useState(false);
    const [isAccepted, setIsAccepted] = useState(false);
    const [answerId, setAnswerId] = useState('');
    const [assessmentId, setAssessmentId] = useState(false);
    const [doneAnswerId, setDoneAnswerId] = useState(false);
    const [errorModal, setErrorModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [detailResolved, setDetailResolved] = useState(false);
    const [assessmentStatus, setAssessmentStatus] = useState('');
    const [pendingAssessmentPopup, setPendingAssessmentPopup] = useState(false);

    const assessment_mode = sessionStorage.getItem('skinAssessmentMode');
    const customerData = sessionStorage.getItem('customer');

    useEffect(() => {
        const didMount = async () => {
            if (hasDataFetched.current || !customerData) return;

            const customer = JSON.parse(customerData);
            if (![BusinessNatureType.BusinessOwner, BusinessNatureType.Member].includes(customer?.type)) {
                setErrorModal(true);
                return;
            }
            hasDataFetched.current = true;
            await fetchAssessmentDetail();
        };
        didMount();
    }, [customerData]);

    useEffect(() => {
        sessionStorage.removeItem('skinAssessmentGender');
        sessionStorage.removeItem('skinAssessmentAge');
    }, [])

    useEffect(() => {
        if (assessment_mode === ASSESSMENT_MODES.anonymous) {
            dispatch(resetSkinAssessmentAnswers());
        }
    }, [assessment_mode])

    const fetchAssessmentDetail = async () => {
        try {
            let assessment_id = (urlParams.get('assessment_id'));
            setLoading(true);
            dispatch(getHnWAssessmentList(assessment_id)).then(res => {

                assessment_id = assessment_id ?? (res?.assessment_id);
                const uid = urlParams.get('uid') ?? '';
                const customer = JSON.parse(sessionStorage.getItem('customer'));
                if (customer?.uid && uid && customer?.uid !== uid) {
                    setErrorModal(true);
                }
                setAssessmentId(assessment_id);
                setLoading(false);

                Promise.all([
                    dispatch(getHnWAssessmentHistoryLog(assessment_id)),
                    dispatch(getHnWAssessmentDetail(assessment_id))
                ]).then(([historyRes, detailRes]) => {

                    if (historyRes && historyRes.length) {
                        setDoneAnswerId(historyRes[0]._id);
                    }

                    setAnswerId(detailRes?.answer_id);
                    setAssessmentStatus(detailRes?.assessments_status);

                    setDetailResolved(true);
                }).catch(function (error) {
                    console.log(error);
                    setDetailResolved(true);
                });
            }).catch(function (error) {
                console.log(error);
            });
        } catch (error) {
            setLoading(false);
        }
    }

    const redirectToAssessment = (assessment_id = assessmentId, startOver = false) => {
        const lastMode = sessionStorage.getItem('skinAssessmentMode');
        if (lastMode === ASSESSMENT_MODES.anonymous) {
            dispatch(resetSkinAssessmentAnswers());
        }
        sessionStorage.setItem('skinAssessmentMode', ASSESSMENT_MODES.normal);
        dispatch(resetAssessmentScore());
        const query = startOver ? `?assessment_id=${assessment_id}&startOver=${startOver}` : `?assessment_id=${assessment_id}`;
        routeTo('pathSkinAssessmentQuestion', query);
    };

    const onStartAssessmentClick = () => {
        if (assessmentStatus === ASSESSMENT_STATUS.pending) {
            setPendingAssessmentPopup(true);
            return;
        }

        if (assessmentStatus === ASSESSMENT_STATUS.done) {
            dispatch(resetSkinAssessmentAnswers());
        }

        redirectToAssessment();
    }

    const redirectToAssessmentAnonymous = (assessment_id = assessmentId) => {
        sessionStorage.setItem('skinAssessmentMode', ASSESSMENT_MODES.anonymous);

        dispatch(resetAssessmentScore());
        const anonId = generateAnonymousId();

        routeTo('pathSkinAssessmentQuestion', `?assessment_id=${assessment_id}&isAnonymous=true&customer_id=${anonId}`);
    };

    const redirectToAssessmentScore = () => {
        routeTo('pathSkinAssessmentResult', `?assessment_id=${assessmentId}&answer_id=${doneAnswerId}`);
    };

    const deleteAssessmentDetail = async () => {
        try {
            setLoading(true);
            dispatch(deleteHnWAssessmentDetail(answerId)).then(res => {
                setLoading(false);
                if (res?.success) {
                    redirectToAssessment(assessmentId, true);
                }
            }).catch(function (error) {
                setLoading(false);
                console.log(error);
            });
        }
        catch (error) {
            console.log(error);
        }
    }

    const closeLiffWindow = async () => {
        await liff.closeWindow();
    }

    const generateAnonymousId = () => {
        return `anonymous_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
    }

    return (
        <>
            <section className="skin-asm-home-container">
                <div className="skin-asm-header">
                    <SkinAssessmentHeader />
                </div>

                <div className='skin-asm-landing-page'>
                    <div className="skin-asm-landing-page-gradient" />
                    {loading || !assessmentList?.landing_image_url || !detailResolved ?
                        <SkeletonLoader className="skin-asm-landing-page-img-loader" /> :
                        <img src={`${config.S3_URL}${assessmentList?.landing_image_url}`} alt="landing-page-img" className="skin-asm-landing-page-img" />}
                </div>

                <SkinAssessmentHomeFooter
                    detailResolved={detailResolved}
                    assessmentHistory={doneAnswerId}
                    checkboxRef={checkboxRef}
                    isAccepted={isAccepted}
                    onAcceptChange={() => setIsAccepted(!isAccepted)}
                    onOpenModal={() => setShowTerms(true)}
                    redirectToAssessment={onStartAssessmentClick}
                    redirectToAssessmentAnonymous={redirectToAssessmentAnonymous}
                    redirectToAssessmentScore={redirectToAssessmentScore} />

            </section>

            {showTerms &&
                <TermsBottomSheetModal
                    onClose={() => setShowTerms(false)}
                    onAccept={() => {
                        if (checkboxRef.current) {
                            checkboxRef.current.checked = true;
                        }
                        setIsAccepted(true);
                        setShowTerms(false);
                    }}
                />
            }
            {pendingAssessmentPopup &&
                <SkinAssessmentErrorModal
                    isOpen={pendingAssessmentPopup}
                    title={SKIN_ASM_CONSTANTS.START_ASSESSMENT_POPUP_TITLE}
                    message={SKIN_ASM_CONSTANTS.PENDING_ASSESSMENT_POPUP_MESSAGE}
                    onClose={() => setPendingAssessmentPopup(false)}
                    buttons={[
                        {
                            label: SKIN_ASM_CONSTANTS.CONTINUE_POPUP_BTN_TEXT,
                            variant: 'primary',
                            onClick: () => { setPendingAssessmentPopup(false); redirectToAssessment(); },
                        },
                        {
                            label: SKIN_ASM_CONSTANTS.START_NEW_BTN_TEXT,
                            variant: 'secondary',
                            onClick: () => { deleteAssessmentDetail() },
                        }
                    ]}
                />
            }
            {errorModal &&
                <SkinAssessmentErrorModal
                    isOpen={errorModal}
                    title={SKIN_ASM_CONSTANTS.USER_ERROR_POPUP_TITLE}
                    message={SKIN_ASM_CONSTANTS.USER_ERROR_POPUP_MESSAGE}
                    onClose={() => closeLiffWindow()}
                    buttons={[
                        {
                            label: SKIN_ASM_CONSTANTS.USER_POPUP_CLOSE_BTN_TEXT,
                            variant: 'primary',
                            onClick: () => { closeLiffWindow() },
                        }
                    ]}
                />
            }
        </>
    );
};

export default SkinAssessmentHome;
